import { Component, OnInit, Input } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmDialogComponent } from 'app/confirm-dialog/confirm-dialog/confirm-dialog.component';
import { PostService } from 'app/core/post.service';
import { PostBuilderComponent } from 'app/forum/components/post-builder/post-builder.component';
import { ForumPost } from 'app/forum/models/forum-post';
import { GroupType } from 'app/groups/models/group-type';
import { GroupService } from 'app/groups/services/group.service';
import { Observable, combineLatest } from 'rxjs';
import { take, map, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-scheduled-posts-dialog',
  template: `
    <h2>{{ 'ScheduledPosts' | preventHardcoding }}</h2>
    <div class="scheduled-posts-container">
      <div
        class="post-item"
        *ngFor="
          let post of sortedByPlannedAtPostsWithNames$ | async;
          trackBy: trackByFn
        "
      >
        <mat-divider></mat-divider>
        <div class="post-title-container">
          <div class="group-name">
            {{ post.groupName }} ({{ post.groupType | preventHardcoding }})
          </div>
          <div class="post-title">
            <span>{{ 'Post' | preventHardcoding }}: </span>{{ post.title }}
          </div>
        </div>
        <div class="post-row">
          <div class="post-date">
            <span class="text">{{ 'ScheduledFor' | preventHardcoding }}</span>
            <span class="date">{{
              post?.plannedAt | date : 'dd. MMM yyyy, HH:mm'
            }}</span>
          </div>

          <button
            mat-icon-button
            class="post-actions"
            [matMenuTriggerFor]="postMenu"
          >
            <mat-icon>more_vert</mat-icon>
          </button>
        </div>

        <mat-menu #postMenu="matMenu">
          <button mat-menu-item (click)="editPost(post)">
            <mat-icon>edit</mat-icon>
            {{ 'edit' | preventHardcoding }}
          </button>
          <button mat-menu-item (click)="deletePost(post)">
            <mat-icon>delete</mat-icon>
            {{ 'delete' | preventHardcoding }}
          </button>
        </mat-menu>
      </div>
    </div>
    <div class="dialog-actions">
      <button
        mat-raised-button
        mat-dialog-close
        color="primary"
        matTooltipPosition="above"
        [matTooltip]="'Back' | preventHardcoding"
        (click)="cancel()"
      >
        {{ 'Back' | preventHardcoding }}
      </button>
    </div>
  `,
  styleUrls: ['./scheduled-posts-dialog.component.scss'],
})
export class ScheduledPostsDialogComponent implements OnInit {
  groupedPlannedPostsWithNames$: Observable<
    {
      groupId: string;
      groupType: GroupType;
      groupName: string;
      posts: ForumPost[];
    }[]
  >;
  sortedByPlannedAtPostsWithNames$: Observable<ForumPost[]>;

  constructor(
    public dialog: MatDialog,
    private dialogRef: MatDialogRef<ScheduledPostsDialogComponent>,
    private postService: PostService,
    private groupService: GroupService
  ) {}

  ngOnInit() {
    this.sortedByPlannedAtPostsWithNames$ = this.postService
      .getAllUserPlannedPosts()
      .pipe(
        switchMap((posts) => {
          const lookups = posts.map((p) =>
            this.groupService
              .getGroupName$(p.groupId, p.groupType)
              .pipe(map((groupName) => ({ ...p, groupName })))
          );
          return combineLatest(lookups);
        }),
        map((posts) =>
          posts.sort((a, b) => a.plannedAt.getTime() - b.plannedAt.getTime())
        )
      );
  }

  getGroupName$(key: string, groupType: GroupType): Observable<string> {
    return this.groupService.getGroupName$(key, groupType);
  }

  editPost(post: ForumPost) {
    const dialogRef = this.dialog.open(PostBuilderComponent, {
      width: 'calc(-24px + 100vw)',
      maxWidth: '840px',
      panelClass: 'create-post-dialog',
      disableClose: true,
    });
    dialogRef.componentInstance.post = post;
  }

  async deletePost(post: ForumPost) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {});
    dialogRef.componentInstance.title = 'deletePlannedPostTitle';
    dialogRef.componentInstance.text = 'deletePlannedPostBody';
    dialogRef.componentInstance.yesText = 'yes';
    dialogRef.componentInstance.noText = 'no';

    return new Promise((resolve) => {
      dialogRef
        .afterClosed()
        .pipe(take(1))
        .toPromise()
        .then(async (yesDelete) => {
          if (yesDelete) {
            await this.postService.removePlannedPost(post);
          }
        });
    });
  }

  cancel() {
    this.dialogRef.close();
  }

  trackByFn(index, item) {
    return item ? item.key : index;
  }
}
